import { ChevronDownIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import classNames from '../../../utils/classNames';

type AxeId = 'axe1' | 'axe2';

const AXES: {
  id: AxeId;
  number: number;
  color: { bg: string; border: string; badge: string; badgeBorder: string; badgeText: string; openBtn: string };
  title: string;
  badge: string;
  detail: string;
}[] = [
  {
    id: 'axe1',
    number: 1,
    color: {
      bg: 'bg-green-600',
      border: 'border-l-green-600',
      badge: 'bg-green-50',
      badgeBorder: 'border-green-200',
      badgeText: 'text-green-600',
      openBtn: 'bg-green-600',
    },
    title:
      "Le premier axe a pour objectif d'installer durablement la notoriété du référent mutation-location auprès des copropriétaires",
    badge: '3 actions dans le planning aux points <strong>1, 2 et 3</strong>',
    detail:
      "Il s'appuie sur des newsletters trimestrielles, trois e-mails de présentation de ses services, ainsi que sur des supports de communication diffusés lors des assemblées générales. Ces actions complémentaires permettent de faire connaître son rôle, de valoriser l'accompagnement qu'il propose et d'ancrer progressivement ce réflexe dans l'esprit des copropriétaires, afin de favoriser les appels entrants quand ils ont un projet immobilier.",
  },
  {
    id: 'axe2',
    number: 2,
    color: {
      bg: 'bg-violet-600',
      border: 'border-l-violet-600',
      badge: 'bg-fuchsia-50',
      badgeBorder: 'border-violet-200',
      badgeText: 'text-violet-600',
      openBtn: 'bg-violet-600',
    },
    title:
      'Le second axe repose sur des actions concrètes de terrain destinées à identifier et contacter directement les copropriétaires ayant un projet immobilier de vente ou qui sont des cibles pertinentes pour la gestion locative',
    badge: '4 actions dans le planning aux points <strong>4, 5, 6 et 7</strong>',
    detail:
      "Il comprend la détection hebdomadaire, chaque mercredi, des nouveaux DPE réalisés sur votre parc d'immeubles, trois campagnes ciblées auprès des SCI, deux opérations de boîtage dans les résidences à fort potentiel, ainsi qu'une méthodologie de valorisation des biens déjà sous mandat auprès des copropriétaires de l'immeuble. L'objectif est de créer des occasions de contact qualifiées, de détecter les intentions de vente ou de mise en location et de transformer cette proximité en mandats concrets.",
  },
];

interface DashboardStrategySectionProps {
  sectionTitle?: string;
  showPlanningToggle?: boolean;
  isPlanningOpen?: boolean;
  onTogglePlanning?: () => void;
}

function DashboardStrategySection({
  sectionTitle = 'La stratégie CROISSANCE',
  showPlanningToggle = false,
  isPlanningOpen = true,
  onTogglePlanning,
}: DashboardStrategySectionProps) {
  const [openAxe, setOpenAxe] = useState<AxeId | null>(null);

  const toggleAxe = (axe: AxeId) => {
    setOpenAxe((prev) => (prev === axe ? null : axe));
  };

  return (
    <div>
      {/* Section header */}
      <div className="mb-4 flex items-center justify-between rounded-[10px] bg-gradient-to-r from-rgpd-blue-dark to-rgpd-blue px-5 py-3.5 shadow-md">
        <div className="flex flex-1 items-center gap-3">
          <div>
            <div className="text-base font-extrabold text-white">{sectionTitle}</div>
            <div className="mt-1 flex items-center gap-2.5">
              <span className="text-[11px] font-bold text-white">
                7 <span className="font-medium text-white/70">Actions planifiées</span>
              </span>
              <span className="text-[11px] text-white/30">|</span>
              <span className="text-[11px] font-bold text-white">
                12 <span className="font-medium text-white/70">Mois de stratégie</span>
              </span>
              <span className="text-[11px] text-white/30">|</span>
              <span className="text-[11px] font-bold text-white">
                100% <span className="font-medium text-white/70">RGPD conforme</span>
              </span>
            </div>
          </div>
        </div>

        {showPlanningToggle && onTogglePlanning && (
          <button
            type="button"
            onClick={onTogglePlanning}
            className="flex shrink-0 items-center gap-1.5 rounded-lg border border-white/40 bg-white/20 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-white/30"
          >
            <span role="img" aria-label="calendar">🗓</span>
            {isPlanningOpen ? 'Réduire' : 'Étendre'}
            <ChevronDownIcon
              className={classNames(
                'h-3 w-3 transition-transform duration-200',
                !isPlanningOpen && '-rotate-90',
              )}
            />
          </button>
        )}
      </div>

      {/* Strategy content */}
      <div className="rounded-[14px] border border-gray-200 bg-white px-8 py-7 shadow-md">
        <h2 className="mb-3 text-[17px] font-extrabold text-rgpd-blue-dark">
          <span role="img" aria-label="clipboard">📋</span> Présentation de la stratégie CROISSANCE
        </h2>

        <p className="mb-5 text-[13px] leading-[1.7] text-gray-600">
          Votre stratégie de communication repose sur{' '}
          <strong className="text-rgpd-blue-dark">2 axes complémentaires</strong> déployés sur 12 mois à travers{' '}
          <strong className="text-rgpd-blue-dark">7 actions concrètes</strong>, pour faire du référent
          mutation-location un interlocuteur identifié et générer davantage d&apos;opportunités.
        </p>

        {AXES.map((axe) => {
          const isOpen = openAxe === axe.id;

          return (
            <div
              key={axe.id}
              className={classNames(
                'overflow-hidden rounded-lg border border-l-4 border-gray-200',
                axe.color.border,
                axe.id === 'axe1' ? 'mb-2.5' : 'mb-5',
              )}
            >
              <div className="flex items-center justify-between gap-3.5 bg-gray-50 px-4 py-3.5">
                <div className="flex items-center gap-3">
                  <div
                    className={classNames(
                      'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[13px] font-black text-white',
                      axe.color.bg,
                    )}
                  >
                    {axe.number}
                  </div>
                  <div>
                    <span className="block text-[13px] leading-snug font-bold text-slate-900">{axe.title}</span>
                    <span
                      className={classNames(
                        'mt-1.5 inline-block rounded-md border px-2.5 py-1 text-[11.5px] font-semibold',
                        axe.color.badge,
                        axe.color.badgeBorder,
                        axe.color.badgeText,
                      )}
                      dangerouslySetInnerHTML={{ __html: `📌 ${axe.badge}` }}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => toggleAxe(axe.id)}
                  className={classNames(
                    'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2 text-[11px] font-bold text-white transition-colors',
                    isOpen ? axe.color.openBtn : 'bg-gray-500 hover:bg-gray-600',
                  )}
                >
                  {isOpen ? (
                    <>
                      Masquer <XMarkIcon className="h-3 w-3" />
                    </>
                  ) : (
                    <>
                      Voir le détail <ChevronRightIcon className="h-3 w-3" />
                    </>
                  )}
                </button>
              </div>

              {isOpen && (
                <div className="border-t border-gray-200 bg-white px-4 py-3.5 pl-16 text-[13px] leading-[1.7] text-gray-600">
                  {axe.detail}
                </div>
              )}
            </div>
          );
        })}

        <p className="text-[13px] leading-[1.7] text-gray-600">
          Cette stratégie s&apos;appuie sur un planning structuré, personnalisable et 100 % conforme RGPD, avec{' '}
          <strong className="text-rgpd-blue-dark">2 points de suivi annuels</strong> pour piloter les résultats et
          ajuster les actions.
        </p>
      </div>
    </div>
  );
}

export default DashboardStrategySection;
